import { CirclePlus, Laptop, LogOut, Menu, Moon, Sun, UserRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { GlobalSearch } from "@/components/common/GlobalSearch";
import { NotificationsMenu } from "@/components/common/NotificationsMenu";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useTheme } from "@/hooks/useTheme";
import { useToast } from "@/hooks/useToast";
import { getInitials } from "@/lib/formatters";

export function AppHeader({ onOpenMobile }: { onOpenMobile: () => void }) {
  const { usuario, authUser, signOut } = useAuth();
  const { hasPermission } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const name = usuario?.nome || authUser?.email?.split("@")[0] || "Usuário";

  async function handleSignOut() {
    try {
      await signOut();
      toast.success("Sessão encerrada com segurança.");
      navigate("/login", { replace: true });
    } catch {
      toast.error("Não foi possível encerrar a sessão.");
    }
  }

  return (
    <header className="sticky top-0 z-30 flex h-18 items-center gap-2 border-b bg-background/92 px-3 shadow-sm backdrop-blur-xl sm:gap-3 md:px-6">
      <Button className="lg:hidden" size="icon" variant="ghost" onClick={onOpenMobile} aria-label="Abrir menu"><Menu className="size-5" /></Button>
      <GlobalSearch />
      <div className="ml-auto flex items-center gap-0.5 sm:gap-1.5">
        {hasPermission("equipamentos_criar") && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" aria-label="Atalhos rápidos"><CirclePlus className="size-[18px]" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasPermission("equipamentos_criar") && <DropdownMenuItem onSelect={() => navigate("/equipamentos/novo")}><Laptop className="size-4" />Novo equipamento</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button size="icon" variant="ghost" onClick={toggleTheme} aria-label={theme === "dark" ? "Ativar tema claro" : "Ativar tema escuro"}>
          {theme === "dark" ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
        </Button>
        <NotificationsMenu />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-11 gap-3 px-2">
              <Avatar><AvatarImage src={usuario?.avatar_url ?? undefined} alt={name} /><AvatarFallback>{getInitials(name)}</AvatarFallback></Avatar>
              <span className="hidden text-left sm:block"><strong className="block max-w-32 truncate text-xs">{name}</strong><small className="block text-[10px] font-normal text-muted-foreground">{usuario?.perfil?.nome ?? "Perfil pendente"}</small></span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => navigate("/configuracoes")}><UserRound className="size-4" />Meu perfil</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => void handleSignOut()}><LogOut className="size-4" />Sair</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
